import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "react-router-dom";
import {
  LEGAL_DOCUMENTS,
  type LegalDocumentKey,
} from "@/lib/legal/documents";
import { SNotFound } from "@/components/Error/SNotFound";

/**
 * `/legal/:doc` — the destination the consent checkboxes have always claimed
 * to have.
 *
 * Before 2026-09-06 `/legal/terms` and `/legal/privacy` matched no route at
 * all, so both links from the two registration forms fell through to the
 * catch-all and showed the not-found page. The user was asked to accept
 * documents that the product could not show them.
 *
 * Two outcomes, and the second one is the point:
 *   - published document → send the browser to the public page. A redirect
 *     rather than an embedded copy, so there is exactly ONE authoritative text
 *     and no second copy in this bundle to drift from it.
 *   - unpublished document → say so plainly, name the document, and give the
 *     address it can be requested from. That is an honest answer; a 404 is not,
 *     and neither is a link to a page that does not exist.
 *
 * An unknown key still renders the ordinary not-found page — `/legal/nonsense`
 * is a wrong URL, not a missing document.
 */
export const LegalDocumentPage = () => {
  const t = useTranslations("legal");
  const { doc } = useParams<{ doc: string }>();

  const entry = LEGAL_DOCUMENTS.find(
    (d) => d.key === (doc as LegalDocumentKey),
  );

  // Redirect in an effect, not during render: a render-phase assignment to
  // window.location fires twice under StrictMode and races the router.
  useEffect(() => {
    if (entry?.href) window.location.replace(entry.href);
  }, [entry]);

  useEffect(() => {
    window.dispatchEvent(new Event("navigationEnd"));
  }, []);

  if (!entry) return <SNotFound />;

  const label = t(entry.labelKey);

  if (entry.href) {
    // Visible only for the instant before the redirect lands, and by screen
    // readers if it stalls. Not a spinner: it names where the user is going.
    return (
      <div
        data-testid="legal-document-redirect"
        className="flex flex-col items-center justify-center h-full w-full grow py-16 gap-2"
      >
        <p className="body-2-regular font-tertiary text-center">
          {t("redirecting", { document: label })}
        </p>
        <a href={entry.href} className="body-2-bold underline font-primary">
          {entry.href}
        </a>
      </div>
    );
  }

  return (
    <div
      data-testid="legal-document-pending"
      className="flex flex-col items-center justify-center h-full w-full grow py-16 gap-4"
    >
      <div className="flex flex-col items-center gap-2 max-w-md">
        <h2 className="body-1-bold font-primary text-center">{label}</h2>
        <p className="body-2-regular font-tertiary text-center">
          {t("notPublished")}
        </p>
        <a
          href="mailto:info@veltric.app"
          className="body-2-bold underline font-primary"
        >
          info@veltric.app
        </a>
        <p className="body-3-regular font-tertiary text-center">
          {t("version", { version: entry.version })}
        </p>
      </div>
    </div>
  );
};
